// TIME_ZONE_PLAN (Grant 2026-10-29): every time is on the CLUB's clock, for every club, and the clock is named only
// when the reader is on a different one: "19:00 UK time" inline on a single headline time, one quiet "Times are in
// UK time" note under the title of a screen whose times all come from one club, per line only in lists that mix
// clubs. Someone at home sees the screens of today. The zone names are the API's ClubTimeZones.Label and the app's
// ClubTime.zoneLabel (the same table, the shared test pins all three). Date-only values are never labelled.
// The device's zone is only known in the browser, so a server render never carries a label (useClubClock adds it
// after mount).

const GENERIC_NAMES = Object.freeze({
  'America/New_York': 'Eastern', 'America/Detroit': 'Eastern',
  'America/Toronto': 'Eastern', 'America/Montreal': 'Eastern',
  'America/Indiana/Indianapolis': 'Eastern',
  'America/Kentucky/Louisville': 'Eastern', 'America/Cancun': 'Eastern',
  'America/Chicago': 'Central', 'America/Winnipeg': 'Central',
  'America/Regina': 'Central', 'America/Mexico_City': 'Central',
  'America/Monterrey': 'Central', 'America/Merida': 'Central',
  'America/Denver': 'Mountain', 'America/Edmonton': 'Mountain',
  'America/Boise': 'Mountain', 'America/Phoenix': 'Mountain',
  'America/Los_Angeles': 'Pacific', 'America/Vancouver': 'Pacific',
  'America/Tijuana': 'Pacific', 'America/Anchorage': 'Alaska',
  'Pacific/Honolulu': 'Hawaii', 'America/Halifax': 'Atlantic',
  'America/Moncton': 'Atlantic', 'America/St_Johns': 'Newfoundland',
  'America/Sao_Paulo': 'Brasilia', 'America/Manaus': 'Amazon',
  'Australia/Sydney': 'Eastern Australia',
  'Australia/Melbourne': 'Eastern Australia',
  'Australia/Brisbane': 'Eastern Australia',
  'Australia/Hobart': 'Eastern Australia',
  'Australia/Adelaide': 'Central Australia',
  'Australia/Darwin': 'Central Australia',
  'Australia/Perth': 'Western Australia',
  'Pacific/Auckland': 'New Zealand', 'Pacific/Chatham': 'Chatham',
  'Europe/Madrid': 'Central European', 'Africa/Ceuta': 'Central European',
  'Atlantic/Canary': 'Western European', 'Europe/Lisbon': 'Western European',
  'Atlantic/Madeira': 'Western European', 'Atlantic/Azores': 'Azores',
  'Europe/Moscow': 'Moscow', 'Europe/Kiev': 'Eastern European',
  'Europe/Kyiv': 'Eastern European',
});

/** "UK time", "Eastern time (New York)", "Paris time": how a line names a zone's clock (the API's ClubTimeZones.Label). */
export function zoneLabel(zone) {
  const z = String(zone ?? '').trim();
  if (!z) return '';
  if (z.toLowerCase() === 'europe/london') return 'UK time';
  const city = z.slice(z.lastIndexOf('/') + 1).replace(/_/g, ' ');
  const name = GENERIC_NAMES[z];
  return name ? `${name} time (${city})` : `${city} time`;
}

/** Whether Intl knows the zone (a blank or made-up id is "unknown": the device clock, no label). */
export function knownZone(zone) {
  if (!zone) return false;
  try {
    new Intl.DateTimeFormat('en-GB', {timeZone: zone});
    return true;
  } catch {
    return false;
  }
}

/** The device's IANA zone in a browser; null on the server or when it can't be read. */
export function deviceZoneId() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

const wallParts = (instant, zone) => {
  const p = new Intl.DateTimeFormat('en-GB', {
    timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date(instant));
  const g = (k) => parseInt(p.find(x => x.type === k)?.value ?? '0', 10);
  return {y: g('year'), mo: g('month'), d: g('day'), h: g('hour') % 24, mi: g('minute')};
};

/** Minutes the zone's clock is ahead of UTC at the instant (null for an unknown zone). */
export function offsetMinutes(instant, zone) {
  if (!knownZone(zone)) return null;
  const t = new Date(instant).getTime();
  if (Number.isNaN(t)) return null;
  const w = wallParts(t, zone);
  const asUtc = Date.UTC(w.y, w.mo - 1, w.d, w.h, w.mi);
  return Math.round((asUtc - Math.floor(t / 60000) * 60000) / 60000);
}

/**
 * An instant as the wall clock of a zone reads it: {date: 'yyyy-MM-dd', time: 'HH:mm'} (the halves a date field and a
 * time select hold). An unknown zone reads on the device clock. Null for nothing.
 */
export function wallClockInZone(instant, zone) {
  if (!instant) return null;
  const t = new Date(instant).getTime();
  if (Number.isNaN(t)) return null;
  const w = wallParts(t, knownZone(zone) ? zone : undefined);
  const pad = (n) => String(n).padStart(2, '0');
  return {date: `${w.y}-${pad(w.mo)}-${pad(w.d)}`, time: `${pad(w.h)}:${pad(w.mi)}`};
}

/**
 * The instant at `date` (yyyy-MM-dd) `time` (HH:mm) on a zone's clock, as ISO; the inverse of wallClockInZone. A missing
 * or unreadable date is null; a missing time is midnight; an unknown zone is the device clock.
 */
export function instantFromWallClock(date, time, zone) {
  const d = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(date ?? ''));
  if (!d) return null;
  const tm = /^(\d{1,2}):(\d{2})$/.exec(String(time ?? '').trim());
  const [y, mo, day] = [Number(d[1]), Number(d[2]), Number(d[3])];
  const [h, mi] = tm ? [Number(tm[1]), Number(tm[2])] : [0, 0];
  if (!knownZone(zone)) {
    const local = new Date(y, mo - 1, day, h, mi, 0, 0);
    return Number.isNaN(local.getTime()) ? null : local.toISOString();
  }
  // The zone's offset at that wall time: read it at the UTC guess, then once more at the corrected instant (DST edges).
  const guess = Date.UTC(y, mo - 1, day, h, mi);
  let instant = guess - (offsetMinutes(guess, zone) ?? 0) * 60000;
  instant = guess - (offsetMinutes(instant, zone) ?? 0) * 60000;
  return new Date(instant).toISOString();
}

/** The two clocks read the instant differently (both zones known). */
export function clocksDiffer(instant, zone, deviceZone) {
  const a = offsetMinutes(instant, zone);
  const b = offsetMinutes(instant, deviceZone);
  return a != null && b != null && a !== b;
}

/**
 * "UK time" when the device's clock reads the instant differently from the club's (the reader is away); null when
 * they agree, or either zone is unknown. The label is the API's own (`label`) when a response carries one.
 */
export function awayLabel(instant, zone, {deviceZone = null, label = null} = {}) {
  if (!instant || !clocksDiffer(instant, zone, deviceZone)) return null;
  return label || zoneLabel(zone);
}

/**
 * A screen's times all come from one club: away when any of the instants reads differently on the device, or, with no
 * instants to look at, when the clocks differ now or at either end of the year (Morocco and the UK agree in summer only).
 */
export function screenAway(zone, instants = [], {deviceZone = null, now = Date.now()} = {}) {
  if (!knownZone(zone) || !knownZone(deviceZone)) return false;
  const list = (instants ?? []).filter(Boolean);
  if (list.length) return list.some(i => clocksDiffer(i, zone, deviceZone));
  const y = new Date(now).getUTCFullYear();
  return [now, Date.UTC(y, 0, 15, 12), Date.UTC(y, 6, 15, 12)].some(i => clocksDiffer(i, zone, deviceZone));
}

export const CLOCK_NOTE_PREFIX = 'Times are in ';

/** The one-line screen note, "Times are in UK time", or null at home. */
export function clockNote(zone, instants = [], {deviceZone = null, label = null, now = Date.now()} = {}) {
  if (!screenAway(zone, instants, {deviceZone, now})) return null;
  return `${CLOCK_NOTE_PREFIX}${label || zoneLabel(zone)}`;
}

/**
 * The instant on the club's clock. style: 'short' = "Tue 3 Nov, 19:00"; 'long' = "Tuesday 3 November 2026, 19:00";
 * 'time' = "19:00". An unknown zone reads on the device clock (the same as every card today).
 */
export function whenInZone(instant, zone, style = 'short') {
  if (!instant) return '';
  const d = new Date(instant);
  if (Number.isNaN(d.getTime())) return '';
  const tz = knownZone(zone) ? zone : undefined;
  const parts = (opts) => {
    const p = new Intl.DateTimeFormat('en-GB', {timeZone: tz, hourCycle: 'h23', ...opts}).formatToParts(d);
    return (k) => p.find(x => x.type === k)?.value ?? '';
  };
  const g = parts({weekday: style === 'long' ? 'long' : 'short', day: 'numeric', month: style === 'long' ? 'long' : 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'});
  const time = `${g('hour')}:${g('minute')}`;
  if (style === 'time') return time;
  if (style === 'long') return `${g('weekday')} ${g('day')} ${g('month')} ${g('year')}, ${time}`;
  return `${g('weekday')} ${g('day')} ${g('month')}, ${time}`;
}

/** D7: "That's 14:00 Tue your time (New York)" for a hover on the label; null when the device zone is unknown. */
export function yourTimeLine(instant, deviceZone) {
  if (!instant || !knownZone(deviceZone)) return null;
  const d = new Date(instant);
  const p = new Intl.DateTimeFormat('en-GB', {timeZone: deviceZone, weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23'}).formatToParts(d);
  const g = (k) => p.find(x => x.type === k)?.value ?? '';
  const city = String(deviceZone).slice(String(deviceZone).lastIndexOf('/') + 1).replace(/_/g, ' ');
  return `That's ${g('hour')}:${g('minute')} ${g('weekday')} your time (${city})`;
}
